import { useRef, useState } from "react";

import { apiCall } from "api/axios";
import { capitalizeWords, formatDateToYYYYMMDD } from "utils";

import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { IDropDownRef } from "ui/dropdown/types";
import { IExportDateRangeValues, TExportType } from "ui/export/types";

import { IApiResponseData } from "../api/axios/types";

export enum ExportDataEnum {
  WALLET_TRANSACTION = "transaction",
  GOAL_SAVINGS = "goal",
  GOAL_SAVING_TRANSACTIONS = "goal_transactions",
  FLEX_SAVINGS = "flex",
  FLEX_SAVING_TRANSACTIONS = "flex_transactions",
  PERSONAL_VAULT_SAVINGS = "personal_vault",
  PERSONAL_VAULT_SAVING_TRANSACTIONS = "personal_vault_transactions",
  BUSINESS_VAULT_SAVINGS = "business_vault",
  BUSINESS_VAULT_SAVING_TRANSACTIONS = "business_vault_transactions",
  EMPLOYER = "employer",
  COMPLIANCE = "compliance",
  EMPLOYEE = "employee",
  USER = "user",
  POINT_ACTIVITY = "point_activity",
  GIFT_POINT = "gift_point",
  REWARDS = "rewards",
  REDEEMED_REWARDS = "redeemed_rewards",
  FILTER_REWARDS = "filter_rewards",
  WALLET = "wallet",
  WITHDRAWAL = "withdrawal",
}

export enum ExportDataFileFormatOptionsType {
  CSV = "csv",
  PDF = "pdf",
}

export interface IExportDataPayload {
  startDate: string;
  endDate: string;
  format: ExportDataFileFormatOptionsType;
  type: ExportDataEnum;
}

export interface IExportDataResponse extends IApiResponseData<null> {}

export const exportData = async (data: IExportDataPayload) => {
  const result = await apiCall({
    apiRoute: "/download",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportDataResponse;
};

export const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];

export interface UseExportDataArgs {
  type: ExportDataEnum;
}

export const useExportData = (props: UseExportDataArgs) => {
  const { type } = props;
  const [isExporting, setIsExporting] = useState(false);
  const dropDownRef = useRef<IDropDownRef | null>(null);

  const [dateRange, setDateRange] = useState<IExportDateRangeValues>({
    startDate: null,
    endDate: null,
  });
  const [exportType, setExportType] = useState<
    ExportDataFileFormatOptionsType | ""
  >("");

  const handleExportTypeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setExportType(value as ExportDataFileFormatOptionsType);
  };

  const handleDateRangeChange = (name: string, value: Date | null) => {
    if (!value) return;
    setDateRange({
      ...dateRange,
      [name]: value,
    });
  };

  const handleResetExport = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
    setExportType("");
  };

  const handleExport = async () => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate || !exportType) return;
    const formattedStartDate = formatDateToYYYYMMDD(startDate);
    const formattedEndDate = formatDateToYYYYMMDD(endDate);
    setIsExporting(true);
    const exportPayload: IExportDataPayload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      format: exportType,
      type,
    };
    try {
      const { error, message } = await exportData(exportPayload);
      if (error) {
        customErrorToast({ message });
      } else if (message) {
        customSuccessToast({ message: capitalizeWords(message) });
        handleResetExport();
        if (dropDownRef.current) {
          dropDownRef.current.handleToggleDropdown();
        }
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsExporting(false);
  };

  return {
    isExporting,
    handleExport,
    handleResetExport,
    handleExportTypeChange,
    handleDateRangeChange,
    dateRange,
    exportType,
    dropDownRef,
  };
};
